import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAction } from 'wasp/client/operations';
import { createOrganizationType } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import {
  Tag,
  Save,
  AlertCircle,
  HelpCircle
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';

// Tooltip component (reusable)
const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

interface OrganizationTypeFormData {
  key: string;
  label: string;
  description: string;
  isActive: boolean;
  hiddenSections: string[];
}

interface FormErrors {
  key?: string;
  label?: string;
  submit?: string;
}

const DIRECTORY_SECTIONS = [
  { key: 'tenants', label: 'Tenants' },
  { key: 'food-court', label: 'Food Court' },
  { key: 'facilities', label: 'Facilities' },
  { key: 'services', label: 'Services' }
];

const COMMUNITY_SECTIONS = [
  { key: 'notices', label: 'Notices' },
  { key: 'events', label: 'Events / Activities' },
  { key: 'buy-sell', label: 'Buy & Sell' },
  { key: 'request-offer', label: 'Request & Offer' },
  { key: 'lost-found', label: 'Lost & Found' }
];

export default function AddOrganizationTypePage() {
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const createOrganizationTypeFn = useAction(createOrganizationType);

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">You must be logged in to add organization types.</p>
        </div>
      </div>
    );
  }

  const [formData, setFormData] = useState<OrganizationTypeFormData>({
    key: '',
    label: '',
    description: '',
    isActive: true,
    hiddenSections: []
  });

  // Auto-generate key from label
  const handleLabelChange = (label: string) => {
    setFormData(f => ({
      ...f,
      label,
      key: label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    }));
    // Clear error when user starts typing
    if (errors.label) {
      setErrors(prev => ({ ...prev, label: undefined }));
    }
  };

  const handleChange = (field: keyof OrganizationTypeFormData, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.label.trim()) {
      newErrors.label = 'Label is required';
    }

    if (!formData.key.trim()) {
      newErrors.key = 'Key is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await createOrganizationTypeFn({
        key: formData.key,
        label: formData.label,
        description: formData.description || undefined,
        isActive: formData.isActive,
        hiddenSections: formData.hiddenSections
      });

      toast({
        title: "Success",
        description: "Organization type created successfully"
      });

      navigate('/admin/organization-types');
    } catch (error: any) {
      console.error('Error creating organization type:', error);
      setErrors({ submit: error.message || 'Failed to create organization type. Please try again.' });
      toast({
        title: "Error",
        description: error.message || 'Failed to create organization type',
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/admin/organization-types');
  };

  return (
    <div className="flex h-full">
        {/* Main Content Area */}
        <div className="flex-1 bg-card">
          <div className="flex h-full">
            {/* Form Content - Left side */}
            <div className="flex-1 pl-6 pt-4">
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Tag className="w-8 h-8 text-blue-600" />
                  <div>
                    <h1 className="text-2xl font-bold text-content-text">Add Organization Type</h1>
                    <p className="text-content-text-muted">Create a new organization type in the system</p>
                  </div>
                </div>
              </div>

              <form id="organization-type-form" onSubmit={handleSubmit}>
                {/* Full-width sections */}
                <div className="space-y-6 mb-6">
                  {/* Primary Input Fields */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-medium text-content-text mb-4">Label *</h3>
                      <input
                        type="text"
                        value={formData.label}
                        onChange={(e) => handleLabelChange(e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder ${
                          errors.label ? 'border-destructive bg-destructive/10' : ''
                        }`}
                        placeholder="e.g., University"
                        required
                      />
                      {errors.label && (
                        <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.label}
                        </p>
                      )}
                    </div>

                    <div>
                      <h3 className="text-lg font-medium text-content-text mb-4">Key *</h3>
                      <input
                        type="text"
                        value={formData.key}
                        onChange={(e) => handleChange('key', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder ${
                          errors.key ? 'border-destructive bg-destructive/10' : ''
                        }`}
                        placeholder="university"
                        required
                      />
                      {errors.key && (
                        <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.key}
                        </p>
                      )}
                      <p className="text-sm text-content-text-muted mt-2">Auto-generated from label, or you can customize it</p>
                    </div>
                  </div>

                  {/* Description Field */}
                  <div>
                    <h3 className="text-lg font-medium text-content-text mb-4">Description</h3>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      rows={4}
                      className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder"
                      placeholder="Provide detailed description of the organization type..."
                    />
                    <p className="text-sm text-content-text-muted mt-2">{formData.description.length}/500 characters</p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    <div className="pb-1 border-b border-gray-100">
                      <h3 className="text-base font-semibold text-gray-900">Default Hidden Sidebar Sections</h3>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Choose the sections to hide by default for organizations created with this type.
                      </p>
                    </div>

                    <div className="space-y-5">
                      <div>
                        <h4 className="text-sm font-medium text-gray-800 mb-2">Directory Sections</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {DIRECTORY_SECTIONS.map(({ key, label }) => (
                            <label key={key} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.hiddenSections.includes(key)}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...formData.hiddenSections, key]
                                    : formData.hiddenSections.filter((s) => s !== key);
                                  handleChange('hiddenSections', next);
                                }}
                                className="w-4 h-4 text-amber-600 border-gray-300 focus:ring-amber-500 rounded"
                              />
                              <span className="text-sm text-gray-700">{label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-sm font-medium text-gray-800 mb-2">Community Sections</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {COMMUNITY_SECTIONS.map(({ key, label }) => (
                            <label key={key} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.hiddenSections.includes(key)}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...formData.hiddenSections, key]
                                    : formData.hiddenSections.filter((s) => s !== key);
                                  handleChange('hiddenSections', next);
                                }}
                                className="w-4 h-4 text-amber-600 border-gray-300 focus:ring-amber-500 rounded"
                              />
                              <span className="text-sm text-gray-700">{label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    <p className="text-xs text-gray-500">
                      These are type-level defaults and can be overridden per organization in organization sidebar settings.
                    </p>
                  </div>
                </div>

                {/* Error message */}
                {errors.submit && (
                  <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {errors.submit}
                  </div>
                )}
              </form>
            </div>

            {/* Right Sidebar - Anchored to the right */}
            <div className="w-80 border-l border-border flex flex-col">
              {/* Action Buttons */}
              <div className="p-6 border-b border-border">
                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    form="organization-type-form"
                    onClick={handleSubmit}
                    disabled={isLoading || !formData.label.trim() || !formData.key.trim()}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Submit
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleGoBack}
                    className="w-full px-4 py-3 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {/* Status Information */}
              <div className="p-6 border-b border-border">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Organization Type Status</h3>

                <div className="space-y-3">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => handleChange('isActive', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900">Active Type</div>
                      <Tooltip content="Mark this organization type as active and visible in the system">
                        <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                  </label>
                </div>
              </div>

              {/* Help Information */}
              <div className="p-6 flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Help</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>• Label and key are required fields</p>
                  <p>• Key is auto-generated from the label</p>
                  <p>• Use descriptive labels like "University", "School", "Company"</p>
                  <p>• Keys should be lowercase and hyphenated</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}



